// Small shared hooks: loading with explicit states, realtime refresh, keyboard shortcuts, focus trapping, per-viewer prefs.
import { useCallback, useEffect, useRef, useState, type DependencyList, type RefObject } from "react";
import type { DataSource } from "../data/source";
import type { ChangeEvent, Topic } from "../data/types";

export interface Loaded<T> { data?: T; error?: string; loading: boolean; reload: () => void }

/** Load once per deps; `reload()` fetches again without blanking the current data (no flicker on realtime refresh). */
export function useLoad<T>(fn: () => Promise<T>, deps: DependencyList): Loaded<T> {
  const [state, setState] = useState<{ data?: T; error?: string; loading: boolean }>({ loading: true });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let live = true;
    setState((s) => ({ ...s, loading: true }));
    fn().then((data) => live && setState({ data, loading: false }), (e: Error) => live && setState({ error: e?.message ?? String(e), loading: false }));
    return () => { live = false; };
  }, [...deps, tick]); // eslint-disable-line react-hooks/exhaustive-deps
  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { ...state, reload };
}

/** Subscribe to a realtime topic for the life of the component. */
export function useRealtime(ds: DataSource, topic: Topic, cb: (e: ChangeEvent) => void) {
  const ref = useRef(cb);
  ref.current = cb;
  useEffect(() => ds.subscribe(topic, (e) => ref.current(e)), [ds, topic]);
}

/** How often the live source re-reads what the engine publishes, in case a Realtime event was missed (some read models are not in
 * the Realtime publication, and a dropped socket loses events). */
export const POLL_MS = 20_000;

/** Short polling beside Realtime (live data only — the demo changes only when a test plays the engine): `fn` every `ms` while the
 * page is visible, and at once when it becomes visible again. */
export function usePoll(ds: DataSource, fn: () => void, ms = POLL_MS) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    if (ds.mode !== "supabase" || typeof document === "undefined") return;
    const tick = () => { if (document.visibilityState !== "hidden") ref.current(); };
    const t = window.setInterval(tick, ms);
    document.addEventListener("visibilitychange", tick);
    return () => { window.clearInterval(t); document.removeEventListener("visibilitychange", tick); };
  }, [ds, ms]);
}

const typing = (t: EventTarget | null) => {
  const el = t as HTMLElement | null;
  return !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable);
};

/** Single-key shortcuts (ignored while typing in a field or with a modifier), e.g. { j: next, k: prev }. */
export function useHotkeys(map: Record<string, (e: KeyboardEvent) => void>, enabled = true) {
  const ref = useRef(map);
  ref.current = map;
  useEffect(() => {
    if (!enabled) return;
    const on = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || typing(e.target)) return;
      const f = ref.current[e.key];
      if (f) { e.preventDefault(); f(e); }
    };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [enabled]);
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Keep Tab inside `ref` while open, focus the first field, restore focus on close; Escape calls onClose. */
export function useFocusTrap(ref: RefObject<HTMLElement>, open: boolean, onClose: () => void) {
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!open) return;
    const before = document.activeElement as HTMLElement | null;
    const root = ref.current;
    const first = root?.querySelector<HTMLElement>("[data-autofocus]") ?? root?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? root)?.focus();
    const on = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); close.current(); return; }
      if (e.key !== "Tab" || !root) return;
      const els = [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((x) => x.offsetParent !== null || x === document.activeElement);
      if (!els.length) return;
      const [a, z] = [els[0], els[els.length - 1]];
      if (e.shiftKey && document.activeElement === a) { e.preventDefault(); z.focus(); }
      else if (!e.shiftKey && document.activeElement === z) { e.preventDefault(); a.focus(); }
    };
    root?.addEventListener("keydown", on);
    return () => { root?.removeEventListener("keydown", on); before?.focus?.(); };
  }, [open, ref]);
}

/** A per-viewer convenience kept in localStorage (never audit state). Survives a blocked storage by staying in memory. */
export function usePref<T>(key: string, initial: T): [T, (v: T) => void] {
  const [v, setV] = useState<T>(() => {
    try { const s = localStorage.getItem("yvc:" + key); return s ? (JSON.parse(s) as T) : initial; } catch { return initial; }
  });
  const set = useCallback((nv: T) => { setV(nv); try { localStorage.setItem("yvc:" + key, JSON.stringify(nv)); } catch { /* storage blocked: keep in memory */ } }, [key]);
  return [v, set];
}
